import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import Moment from 'moment';
import { Link } from 'react-router-dom';
import axiosInstance from '../../config/axiosApi/axiosApi';
import APIs from '../../Constants/apis';
import showErrorToast from '../../components/CustomComponents/toast/toast';
import GridSectionLoader from '../../components/CustomComponents/preLoad/gridSectionLoader';

function Dashboard() {

  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const { profileData } = useSelector((state) => state.auth);
  console.log("Profile Data from auth", profileData);
  const [compCurrency, setCompCurrency] = useState("GBP");
  const [loading, setLoading] = useState(false);
  const [salesData, setSalesData] = useState(null);
  // Date state for the dashboard - single day
  const [selectedDate, setSelectedDate] = useState(Moment());

  const currencySymbols = {
    GBP: "£",
    USD: "$",
    EUR: "€",
  };

  const getCurrencySymbol = (currencyCode) => {
    return currencySymbols[currencyCode] || currencyCode;
  };


  const fetchCompCurrency = useCallback(async () => {
    try {
      // Use Redux state instead of API call
      if (!profileData) {
        console.log("Profile data not available yet, using default currency");
        setCompCurrency("GBP");
        return;
      }
      setCompCurrency(profileData?.compCurrency || "GBP");
    } catch (err) {
      console.error("Failed to load company currency:", err);
      setCompCurrency("GBP");
    }
  }, [profileData]);


  const fetchTodaysSales = useCallback(async () => {
    if (!CompID) return;

    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_TODAY_SALE, {
        params: {
          CompID: CompID,
          StartDate: selectedDate.format('YYYY-MM-DD'),
          EndDate: selectedDate.format('YYYY-MM-DD')
        }
      });

      if (response.data.status) {
        setSalesData(response.data.data);
      } else {
        showErrorToast(response.data.message || "Failed to fetch today's sales data");
      }
    } catch (err) {
      showErrorToast("Failed to load today's sales data");
      console.error('Error fetching sales data:', err);
    } finally {
      setLoading(false);
    }
  }, [CompID, selectedDate]);

  // Helper function to format currency values
  const formatCurrency = (value) => {
    if (value === null || value === undefined || isNaN(value)) return `${getCurrencySymbol(compCurrency)}0.00`;
    return `${getCurrencySymbol(compCurrency)} ${parseFloat(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Generate dynamic data based on API response or fallback to static data
  const getSalesByTender = () => {
    if (salesData) {
      return [
        { label: 'Sales by Cash', value: formatCurrency(salesData.dS_SalesbyCash), change: '+0%', icon: '💷' },
        { label: 'Sales by Card', value: formatCurrency(salesData.dS_SalesByCard), change: '+0%', icon: '💳' },
        { label: 'Sales by Online', value: formatCurrency(salesData.dS_SalesByOnlineDeposit), change: '+0%', icon: '🌐' },
        { label: 'Sales by Deposit', value: formatCurrency(salesData.dS_SalesByDeposit), change: '+0%', icon: '🏦' },
      ];
    }
    return [
      { label: 'Sales by Cash', value: `${getCurrencySymbol(compCurrency)} 00.00`, icon: '💷' },
      { label: 'Sales by Card', value: `${getCurrencySymbol(compCurrency)} 00.00`, icon: '💳' },
      { label: 'Sales by Online', value: `${getCurrencySymbol(compCurrency)} 00.00`, icon: '🌐' },
      { label: 'Sales by Deposit', value: `${getCurrencySymbol(compCurrency)} 00.00`, icon: '🏦' },
    ];
  };

  const getSaleByCategory = () => {
    if (salesData) {
      const categories = [];

      // Add Menu stats first


      // Add reporting categories from reportCatStats array
      if (salesData.reportCatStats && Array.isArray(salesData.reportCatStats)) {

        salesData.reportCatStats.forEach(category => {
          categories.push({
            label: category.categoryTypeName || 'Unknown Category',
            value: formatCurrency(category.totalSales),
            orders: category.orderCount || 0
          });
        });
      }

      if (salesData.menuStats) {
        categories.push({
          label: 'Menu',
          value: formatCurrency(salesData.menuStats.totalSales),
          orders: salesData.menuStats.orderCount || 0
        });
      }

      if (salesData.comboStats) {
        categories.push({
          label: 'Combo',
          value: formatCurrency(salesData.comboStats.totalSales),
          orders: salesData.comboStats.orderCount || 0
        });
      }


      // Add Combo stats at the end


      return categories;
    }

    return [
      { label: 'Menu', value: `${getCurrencySymbol(compCurrency)} 00.00`, orders: 0 },
      { label: 'Combo', value: `${getCurrencySymbol(compCurrency)} 00.00`, orders: 0 },
    ];
  };

  const getSaleByService = () => {
    if (salesData?.stats) {
      return [
        { label: 'Dine In', value: formatCurrency(salesData.stats.dS_DineInSales), orders: salesData.stats.dS_DineInOrderCount || 0 },
        { label: 'Collection', value: formatCurrency(salesData.stats.dS_TakeAwaySales), orders: salesData.stats.dS_TakeAwayOrderCount || 0 },
        { label: 'Deliveries', value: formatCurrency(salesData.stats.dS_DeliveriesSales), orders: salesData.stats.dS_DeliveryOrderCount || 0 },
        { label: 'Kiosk', value: formatCurrency(salesData.stats.dS_KioskSales), orders: salesData.stats.dS_KioskOrderCount || 0 },
        { label: 'Online Order', value: formatCurrency(salesData.stats.dS_OnlineOrderSales), orders: salesData.stats.dS_OnlineOrderCount || 0 },
        { label: 'Third Party', value: formatCurrency(salesData.stats.dS_ThirdPartySales), orders: salesData.stats.dS_ThirdPartyOrderCount || 0 },
      ];
    }
    return [
      { label: 'Dine In', value: `${getCurrencySymbol(compCurrency)}00.00`, orders: 0 },
      { label: 'Collection', value: `${getCurrencySymbol(compCurrency)}00.00`, orders: 0 },
      { label: 'Deliveries', value: `${getCurrencySymbol(compCurrency)}00.00`, orders: 0 },
      { label: 'Kiosk', value: `${getCurrencySymbol(compCurrency)}00.00`, orders: 0 },
      { label: 'Online Order', value: `${getCurrencySymbol(compCurrency)}00.00`, orders: 0 },
      { label: 'Third Party', value: `${getCurrencySymbol(compCurrency)}00.00`, orders: 0 },
    ];
  };

  const getSaleBy = () => {
    if (salesData?.stats) {
      return [
        {
          label: 'Tip',
          value: formatCurrency(salesData.stats.dS_Tip),
          orders: null
        },
        {
          label: 'Discounts',
          value: formatCurrency(salesData.stats.dS_Discount),
          orders: salesData.stats.dS_DiscountOrderCount !== undefined ? salesData.stats.dS_DiscountOrderCount : null
        },
        {
          label: 'Voucher Discounts',
          value: formatCurrency(salesData.stats.dS_VoucherDiscount),
          orders: salesData.stats.dS_VoucherDiscountOrderCount !== undefined ? salesData.stats.dS_VoucherDiscountOrderCount : null
        },
        {
          label: 'Gift Card Discounts',
          value: formatCurrency(salesData.stats.dS_GiftCardDiscount),
          orders: salesData.stats.dS_GiftCardDiscountOrderCount !== undefined ? salesData.stats.dS_GiftCardDiscountOrderCount : null
        },
        {
          label: 'Void',
          value: formatCurrency(salesData.stats.dS_Void),
          orders: salesData.stats.dS_VoidOrderCount !== undefined ? salesData.stats.dS_VoidOrderCount : null
        },
        {
          label: 'Refund',
          value: formatCurrency(salesData.stats.dS_Refund),
          orders: salesData.stats.dS_RefundOrderCount !== undefined ? salesData.stats.dS_RefundOrderCount : null
        },
        {
          label: 'Service Charges',
          value: formatCurrency(salesData.stats.dS_ServiceCharges),
          orders: null // No order count data available
        },
        {
          label: 'Delivery Charges',
          value: formatCurrency(salesData.stats.dS_DeliveryCharges),
          orders: null // No order count data available
        },
      ];
    }
    return [
      { label: 'Tip', value: `${getCurrencySymbol(compCurrency)}00.00`, orders: null },
      { label: 'Discounts', value: `${getCurrencySymbol(compCurrency)}00.00`, orders: null },
      { label: 'Voucher Discounts', value: `${getCurrencySymbol(compCurrency)}00.00`, orders: null },
      { label: 'Gift Card Discounts', value: `${getCurrencySymbol(compCurrency)}00.00`, orders: null },
      { label: 'Void', value: `${getCurrencySymbol(compCurrency)}00.00`, orders: null },
      { label: 'Refund', value: `${getCurrencySymbol(compCurrency)}00.00`, orders: null },
      { label: 'Service Charges', value: `${getCurrencySymbol(compCurrency)}00.00`, orders: null },
      { label: 'Delivery Charges', value: `${getCurrencySymbol(compCurrency)}00.00`, orders: null },
    ];
  };

  const getTaxBy = () => {
    if (salesData?.stats) {
      return [
        { label: 'Gross Sales', value: formatCurrency(salesData.stats.dS_GrossSales) },
        { label: 'Net Sales', value: formatCurrency(salesData.stats.dS_NetSales) },
        { label: 'VAT Collection', value: formatCurrency(salesData.stats.dS_VATCollection) },
      ];
    }
    return [
      { label: 'Gross Sales', value: `${getCurrencySymbol(compCurrency)}00.00` },
      { label: 'Net Sales', value: `${getCurrencySymbol(compCurrency)}00.00` },
      { label: 'VAT Collection', value: `${getCurrencySymbol(compCurrency)}00.00` },
    ];
  };

  // Handle date change from input
  const handleDateChange = (event) => {
    const newDate = Moment(event.target.value);
    if (newDate.isValid()) {
      setSelectedDate(newDate);
    }
  };

  // Fetch company currency and today's sales on component mount
  useEffect(() => {
    fetchCompCurrency();
    fetchTodaysSales();
  }, [fetchCompCurrency, fetchTodaysSales]);

  // Re-fetch currency when profileData becomes available
  useEffect(() => {
    if (profileData && profileData.compCurrency) {
      setCompCurrency(profileData.compCurrency);
    }
  }, [profileData]);

  // Fetch sales data when date changes
  useEffect(() => {
    if (CompID) {
      fetchTodaysSales();
    }
  }, [selectedDate, CompID, fetchTodaysSales]);

  return (
    <div className="dashboard-container">
      <ol className="breadcrumb float-xl-end">

      </ol>
      <h1 className="page-header mb-3">Dashboard</h1>
      <div className="d-sm-flex align-items-center mb-3">
        <div className="d-flex align-items-center">
          <input
            id="date-picker"
            type="date"
            className="form-control"
            style={{ width: '200px' }}
            value={selectedDate.format('YYYY-MM-DD')}
            onChange={handleDateChange}
          />
        </div>
      </div>
      <GridSectionLoader loading={loading} minHeight="360px">
      <div className="row">
        <div className="col-xl-3 col-md-6">
          <div className="widget widget-stats bg-teal">
            <div className="stats-icon stats-icon-lg"><i className="fa fa-money-bill-wave fa-fw"></i></div>
            <div className="stats-content">
              <div className="stats-title">SALES BY CASH</div>
              <div className="stats-number">{salesData?.stats?.dS_SalesbyCash !== undefined ? formatCurrency(salesData.stats.dS_SalesbyCash) : `${getCurrencySymbol(compCurrency)} 00.00`}</div>
              <div className="stats-progress progress">
                <div className="progress-bar" style={{ width: '70.1%' }}></div>
              </div>
              <div className="stats-desc">{salesData?.stats?.dS_CashOrderCount || 0} Orders</div>
            </div>
          </div>
        </div>
        <div className="col-xl-3 col-md-6">
          <div className="widget widget-stats bg-blue">
            <div className="stats-icon stats-icon-lg"><i className="fa fa-credit-card fa-fw"></i></div>
            <div className="stats-content">
              <div className="stats-title">SALES BY CARD</div>
              <div className="stats-number">{salesData?.stats?.dS_SalesByCard !== undefined ? formatCurrency(salesData.stats.dS_SalesByCard) : `${getCurrencySymbol(compCurrency)} 00.00`}</div>
              <div className="stats-progress progress">
                <div className="progress-bar" style={{ width: '40.5%' }}></div>
              </div>
              <div className="stats-desc">{salesData?.stats?.dS_CardOrderCount || 0} Orders</div>
            </div>
          </div>
        </div>
        <div className="col-xl-3 col-md-6">
          <div className="widget widget-stats bg-indigo">
            <div className="stats-icon stats-icon-lg"><i className="fa fa-laptop fa-fw"></i></div>
            <div className="stats-content">
              <div className="stats-title">SALES BY ONLINE</div>
              <div className="stats-number">{salesData?.stats?.dS_SalesByOnlineDeposit !== undefined ? formatCurrency(salesData.stats.dS_SalesByOnlineDeposit) : `${getCurrencySymbol(compCurrency)} 00.00`}</div>
              <div className="stats-progress progress">
                <div className="progress-bar" style={{ width: '76.3%' }}></div>
              </div>
              <div className="stats-desc">{salesData?.stats?.dS_SalesByOnlineDepositCount || 0} Orders</div>
            </div>
          </div>
        </div>
        <div className="col-xl-3 col-md-6">
          <div className="widget widget-stats bg-dark">
            <div className="stats-icon stats-icon-lg"><i className="fa fa-piggy-bank fa-fw"></i></div>
            <div className="stats-content">
              <div className="stats-title">SALES BY DEPOSIT</div>
              <div className="stats-number">{salesData?.stats?.dS_SalesByDeposit !== undefined ? formatCurrency(salesData.stats.dS_SalesByDeposit) : `${getCurrencySymbol(compCurrency)} 00.00`}</div>
              <div className="stats-progress progress">
                <div className="progress-bar" style={{ width: '54.9%' }}></div>
              </div>
              <div className="stats-desc">{salesData?.stats?.dS_DepositOrderCount || 0} Orders</div>
            </div>
          </div>
        </div>
      </div>
      {/* Sale by Category */}
      <section className="dashboard-section mb-4">
        <h4 className="mb-3">Sale by Category</h4>
        <div className="row">
          {getSaleByCategory().map((item, idx) => (

            <div className="col-xl-3 col-md-6 mb-3" key={idx}>
              <div className="card bg-dark text-white border-0" style={{ backgroundColor: '#2c2c2c' }}>
                <div className="card-body p-3">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div className="text-uppercase text-white-50 fw-bold fs-12px">
                      {item.label}
                    </div>
                  </div>
                  <div className="d-flex align-items-end mb-2">
                    <h2 className="mb-0 text-white">{item.value}</h2>
                  </div>
                  <div className="text-white-50 small" style={{ minHeight: '20px' }}>
                    {item.orders !== null && item.orders !== undefined ? `${item.orders} Orders` : ''}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Sales by Service */}
      <section className="dashboard-section mb-4">
        <h4 className="mb-3">Sales by Service</h4>
        <div className="row">
          {getSaleByService().map((item, idx) => (
            <div className="col-xl-3 col-md-6 mb-3" key={idx}>
              <div className="card bg-dark text-white border-0" style={{ backgroundColor: '#2c2c2c' }}>
                <div className="card-body p-3">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div className="text-uppercase text-white-50 fw-bold fs-12px">
                      {item.label}
                    </div>
                  </div>
                  <div className="d-flex align-items-end mb-2">
                    <h2 className="mb-0 text-white">{item.value}</h2>
                  </div>
                  <div className="text-white-50 small" style={{ minHeight: '20px' }}>
                    {item.orders !== null && item.orders !== undefined ? `${item.orders} Orders` : ''}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Sales by */}
      <section className="dashboard-section mb-4">
        <h4 className="mb-3">Sales by</h4>
        <div className="row">
          {getSaleBy().map((item, idx) => (
            <div className="col-xl-3 col-md-6 mb-3" key={idx}>
              <div className="card bg-dark text-white border-0" style={{ backgroundColor: '#2c2c2c' }}>
                <div className="card-body p-3">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div className="text-uppercase text-white-50 fw-bold fs-12px">
                      {item.label}
                    </div>
                  </div>
                  <div className="d-flex align-items-end mb-2">
                    <h2 className="mb-0 text-white">{item.value}</h2>
                  </div>
                  <div className="text-white-50 small" style={{ minHeight: '20px' }}>
                    {item.orders !== null && item.orders !== undefined ? `${item.orders} Orders` : ''}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Tax by */}
      <section className="dashboard-section mb-4">
        <h4 className="mb-3">Tax by</h4>
        <div className="row">
          {getTaxBy().map((item, idx) => (
            <div className="col-xl-3 col-md-6 mb-3" key={idx}>
              <div className="card bg-dark text-white border-0" style={{ backgroundColor: '#2c2c2c' }}>
                <div className="card-body p-3">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div className="text-uppercase text-white-50 fw-bold fs-12px">
                      {item.label}
                    </div>
                  </div>
                  <div className="d-flex align-items-end mb-2">
                    <h2 className="mb-0 text-white">{item.value}</h2>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
      </GridSectionLoader>
    </div>
  );
}

export default Dashboard;